import { useEffect, useCallback, useState, useMemo, useRef } from "react";
import PubSub from "../../../services/PubSub";
import { useTranslation } from "react-i18next";
import ConfirmationPrompt from "../../controls/ConfirmationPrompt";
import BootstrapDropdown from "react-bootstrap/Dropdown";
import Icon from "../../controls/Icon";
import CreateModal from "../../controls/filePicker/CreateModal";
import SearchBar from "../../controls/SearchBar";
import Toolbar from "../../controls/Toolbar";
import _ from "lodash";

const heading = "OTM_LANGUAGES_HEADER";
const fileRegex = /^[A-Za-z0-9-]+$/;
const configKey = 'languages';
const basePath = '/l10n/';
const defaultFile = '/l10n/en-us.json';

const LanguagesEditor = ({
    initialBuildTimeConfig,
    buildTimeConfig,
    templateConfig,
    onSave,
    onLivePreviewUpdate,
    canSendUpdate
}) => {
    const ref = useRef();
    const [translate] = useTranslation();

    const [searchPrompt, setSearchPrompt] = useState('');

    const [editorData, setEditorData] = useState([]);
    const [searchableData, setSearchableData] = useState([]);

    const [selectedFile, setSelectedFile] = useState("");
    const [hasErrors, setHasErrors] = useState(false);
    const [keyAdded, setKeyAdded] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const matchesSearch = useCallback((data) => {
        if (data.key.toLowerCase().includes(searchPrompt.toLowerCase())) {
            return true;
        }

        if (data.defaultValue !== undefined || data.value !== undefined) {
            if (data.defaultValue !== undefined && data.defaultValue.toLowerCase().includes(searchPrompt.toLowerCase())) {
                return true;
            }
            if (data.value !== undefined && data.value.toLowerCase().includes(searchPrompt.toLowerCase())) {
                return true;
            }
            return false;
        }

        if (data.defaultParentValue !== undefined && data.defaultParentValue.toLowerCase().includes(searchPrompt.toLowerCase())) {
            return true;
        }

        if (data.parentValue !== undefined && data.parentValue.toLowerCase().includes(searchPrompt.toLowerCase())) {
            return true;
        }
    }, [searchPrompt]);

    const saveChanges = useCallback((data, keyToDelete) => {
        const defaultFileResult = {};
        const result = {};
        const keysArr = data.filter(obj => obj.key !== '').map(obj => obj.key);

        if (keysArr.length !== new Set(keysArr).size) {
            const duplicates = [];
            keysArr.forEach((val, i) => { if (keysArr.indexOf(val) !== i) duplicates.push(val) });
            setEditorData(() => data.map(entry => {
                return {
                    ...entry,
                    hasError: duplicates.includes(entry.key)
                }
            }));
            setHasErrors(true);
            PubSub.publish("show_info", translate("OTM_LAGNUAGES_SAVE_FAILED"), translate("OTM_SAVE_FAILED_TITLE"));
            return;
        }

        setHasErrors(false);
        data.forEach(entry => {
            if (entry.newKey) {
                // Add all new entries to all other files
                Object.keys(buildTimeConfig[configKey]).filter(file => ![selectedFile, defaultFile].includes(file))
                    .forEach(file => {
                        buildTimeConfig[configKey][file][entry.key] = '';
                    })
            }
            if (entry.defaultValue) {
                defaultFileResult[entry.key] = entry.defaultValue;
            }
            if (entry.value) {
                result[entry.key] = entry.value;
            }
        });

        Object.keys(buildTimeConfig[configKey])
            .filter(file => ![selectedFile, defaultFile].includes(file))
            .forEach(file => {
                delete buildTimeConfig[configKey][file][keyToDelete];
            });

        setEditorData(() => data.map(entry => {
            return {
                ...entry,
                newKey: undefined,
                hasError: false
            }
        }));

        buildTimeConfig[configKey][defaultFile] = defaultFileResult;
        if (selectedFile) {
            buildTimeConfig[configKey][selectedFile] = result;
        }
        onSave(buildTimeConfig);
    }, [buildTimeConfig, onSave, selectedFile, translate]);

    const handleAddKey = useCallback(() => {
        setEditorData(prev => {
            prev[prev.length] = { key: '', value: '', defaultValue: '', fixed: false, newKey: true }
            return [...prev];
        });
        setSearchPrompt("");
        ref.current.resetSearch();
        setKeyAdded(true);
    }, []);

    const handleDeleteKey = useCallback((index) => {
        const keyToDelete = editorData[index].key;
        editorData.splice(index, 1);
        saveChanges(editorData, keyToDelete);

    }, [editorData, saveChanges])

    const handleOnSelectFile = useCallback((selectedFile) => {
        setSelectedFile(selectedFile);
        if (selectedFile) {
            setEditorData(prev => {
                const templateData = templateConfig[configKey][selectedFile];
                const overridesData = buildTimeConfig[configKey][selectedFile];

                prev = prev.map(obj => {
                    return {
                        ...obj,
                        parentValue: templateData ? templateData[obj.key] : undefined,
                        value: overridesData[obj.key],
                        fixed: templateData && templateData[obj.key] !== undefined ? true : obj.fixed
                    }
                });

                setSearchableData(prev.map(entry => _.clone(entry)));
                return prev;
            });
        }

        if (selectedFile === "") {
            setEditorData(prev => {
                prev = prev.map(obj => ({
                    ...obj,
                    parentValue: undefined,
                    value: undefined,
                    fixed: obj.defaultParentValue !== undefined ? true : false
                }));

                setSearchableData(prev.map(entry => _.clone(entry)));
                return prev;
            });
        }
    }, [templateConfig, buildTimeConfig]);

    const handleAddFile = useCallback((fileName) => {
        const filePath = `${basePath}${fileName}.json`;
        if (buildTimeConfig[configKey][filePath] !== undefined) {
            PubSub.publish("show_info", "OTM_FILE_EXISTS");
            return;
        }
        buildTimeConfig[configKey][filePath] = {};
        onSave(buildTimeConfig);

        setShowCreateModal(false);
    }, [buildTimeConfig, onSave]);

    const handleDeleteFile = useCallback((fileName) => {
        delete buildTimeConfig[configKey][fileName];
        handleOnSelectFile("");
        onSave(buildTimeConfig);
        setShowDeleteModal(false);
    }, [buildTimeConfig, onSave, handleOnSelectFile]);

    const handleRestoreConfirm = useCallback(() => {
        buildTimeConfig[configKey] = initialBuildTimeConfig[configKey];

        setEditorData(() => {
            const data = [];
            const defaultTemplateData = templateConfig[configKey][defaultFile];
            const defaultOverridesData = buildTimeConfig[configKey][defaultFile];

            if (defaultTemplateData) {
                Object.keys(defaultTemplateData).forEach(key => {
                    data.push({
                        key,
                        defaultParentValue: defaultTemplateData[key],
                        defaultValue: defaultOverridesData[key],
                        fixed: true
                    });
                });
            }
            if (defaultOverridesData) {
                Object.keys(defaultOverridesData).forEach(key => {
                    if (!data.find(obj => obj.key === key)) {
                        data.push({
                            key,
                            defaultValue: defaultOverridesData[key],
                            fixed: false
                        });
                    }
                });
            }

            if (selectedFile) {
                const templateData = templateConfig[configKey][selectedFile];
                const overridesData = buildTimeConfig[configKey][selectedFile];
                return data.map(obj => {
                    return {
                        ...obj,
                        parentValue: templateData ? templateData[obj.key] : undefined,
                        value: overridesData[obj.key],
                        fixed: templateData && templateData[obj.key] !== undefined ? true : obj.fixed
                    }
                });
            }

            return data;
        });

        setSearchPrompt("");
        ref.current.resetSearch();
        onSave(buildTimeConfig);
        setShowRestoreModal(false);
    }, [initialBuildTimeConfig, buildTimeConfig, templateConfig, onSave, selectedFile]);

    const handleRestore = useCallback(() => {
        if (!_.isEqual(initialBuildTimeConfig[configKey], buildTimeConfig[configKey])) {
            setShowRestoreModal(true);
        }
        else {
            PubSub.publish("show_info", translate("OTM_NOTHING_TO_RESTORE"), translate("OTM_INFO"));
        }
    }, [buildTimeConfig, initialBuildTimeConfig, translate]);

    const renderDefaultActionButton = useCallback((data, index) => {
        if (!data.fixed) {
            return <button className='btn btn-outline border-0 p-0'
                title={translate("OTM_DELETE_KEY")}
                onClick={() => handleDeleteKey(index)}
                type="button">
                <Icon icon="DeleteOutlined" className="d-block" />
            </button>
        }
        if (data.fixed && data.defaultParentValue !== data.defaultValue && data.defaultValue !== undefined) {
            return <button className='btn btn-outline border-0 p-0'
                title={translate("OTM_RESET_TO_TEMPLATE")}
                onClick={() => {
                    editorData[index].defaultValue = undefined;
                    saveChanges(editorData)
                }}
                type="button">
                <Icon icon="FastRewind" className="d-block" />
            </button>
        }
    }, [editorData, saveChanges, translate, handleDeleteKey]);

    const renderActionButton = useCallback((data, index) => {
        if (!data.fixed && data.value !== undefined) {
            return <button className='btn btn-outline border-0 p-0'
                title={translate("OTM_DELETE_KEY")}
                onClick={() => handleDeleteKey(index)}
                type="button">
                <Icon icon="DeleteOutlined" className="d-block" />
            </button>
        }
        if (data.fixed && data.parentValue !== data.value && data.value !== undefined) {
            return <button className='btn btn-outline border-0 p-0'
                title={translate("OTM_RESET_TO_TEMPLATE")}
                onClick={() => {
                    editorData[index].value = undefined;
                    saveChanges(editorData)
                }}
                type="button">
                <Icon icon="FastRewind" className="d-block" />
            </button>
        }
    }, [editorData, saveChanges, translate, handleDeleteKey]);

    const renderRow = useCallback((data, index) => {
        if (searchableData[index] && !matchesSearch(searchableData[index])) {
            return <></>
        }

        return <div className="row pb-1" key={index}>
            <div className="col-3">
                <textarea
                    type="string"
                    rows={1}
                    value={data.key}
                    disabled={data.fixed}
                    className={`form-control form-control-sm ${data.hasError ? 'border border-danger' : ''}`}
                    onInput={(event) => {
                        setEditorData(prev => {
                            prev[index].key = event.target.value;

                            return [...prev];
                        })
                    }}
                    onBlur={() => saveChanges(editorData)}
                />
            </div>
            <div className="col">
                <textarea
                    type="string"
                    rows={1}
                    value={data.defaultValue !== undefined ? data.defaultValue : data.defaultParentValue || ''}
                    className={`form-control form-control-sm ${data.hasError ? 'border border-danger' : ''}`}
                    onInput={(event) => {
                        setEditorData(prev => {
                            prev[index].defaultValue = event.target.value;

                            return [...prev];
                        })
                    }}
                    onBlur={() => saveChanges(editorData)}
                />
            </div>
            <div className="col-fixed-2 p-0">
                {renderDefaultActionButton(data, index)}
            </div>

            <div className="col">
                <textarea
                    type="string"
                    rows={1}
                    disabled={!selectedFile}
                    value={data.value !== undefined ? data.value : data.parentValue || ''}
                    className={`form-control form-control-sm ${data.hasError && selectedFile ? 'border border-danger' : ''}`}
                    onInput={(event) => {
                        setEditorData(prev => {
                            prev[index].value = event.target.value;

                            return [...prev];
                        })
                    }}
                    onBlur={() => saveChanges(editorData)}
                />
            </div>

            <div className="col-fixed-2 p-0">
                {selectedFile && renderActionButton(data, index)}
            </div>
        </div>
    }, [editorData, searchableData, selectedFile, matchesSearch, saveChanges, renderActionButton, renderDefaultActionButton]);

    const searchBar = useMemo(() => {
        return (
            <div className="p-2 pb-1">
                <SearchBar
                    ref={ref}
                    size='sm'
                    initialValue={searchPrompt}
                    placeholder={translate("OTM_SEARCH_LANGUAGES")}
                    onChange={(value) => setSearchPrompt(value)}
                    onBlur={() => { setSearchableData(editorData.map(entry => _.clone(entry))) }} />
            </div>
        );
    }, [editorData, searchPrompt, translate]);

    const renderDataHeader = useMemo(() => (
        <div className="row">
            <div className="col-3 text-center">
                <label className="h5 text-secondary" style={{ verticalAlign: 'middle' }}>
                    {translate("OTM_KEY")}
                </label>
                <button className='btn btn-outline border-0 p-0'
                    title={translate("OTM_ADD")}
                    onClick={handleAddKey}
                    type="button">
                    <Icon icon="Add" className="d-block" />
                </button>
            </div>
            <div className="col">
                <label className="h5 text-secondary text-center w-100">
                    {defaultFile.replace(basePath, '').replace('.json', '')}
                </label>
            </div>

            <div className="col-fixed-2 p-0">
            </div>

            <div className="col">
                <div className="row justify-content-center">
                    <BootstrapDropdown onSelect={handleOnSelectFile} className="col-auto">
                        <BootstrapDropdown.Toggle variant='white' className='border-0 p-0 align-items-center' disabled={hasErrors}>
                            <label className="h5 text-secondary">
                                {selectedFile.replace(basePath, '').replace('.json', '') || translate("OTM_SELECT")}
                            </label>
                        </BootstrapDropdown.Toggle>
                        <BootstrapDropdown.Menu>
                            {buildTimeConfig && Object.keys(buildTimeConfig[configKey]).filter(el => el !== defaultFile).map(el => (
                                <BootstrapDropdown.Item key={el} id={el} eventKey={el}>
                                    {el.replace(basePath, '').replace('.json', '')}
                                </BootstrapDropdown.Item>
                            ))}
                        </BootstrapDropdown.Menu>
                    </BootstrapDropdown>
                    <button className='col-auto btn btn-outline border-0 p-0' title={translate("OTM_DELETE_FILE")} onClick={() => setShowDeleteModal(true)} type="button" disabled={!selectedFile}>
                        <Icon icon="DeleteOutlined" className="d-block" />
                    </button>
                    <button className='col-auto btn btn-outline border-0 p-0' title={translate("OTM_ADD_FILE")} onClick={() => setShowCreateModal(true)} type="button">
                        <Icon icon="PlaylistAdd" className="d-block" />
                    </button>
                </div>
            </div>

            <div className="col-fixed-2 p-0">
            </div>
        </div>
    ), [buildTimeConfig, selectedFile, hasErrors, handleAddKey, handleOnSelectFile, translate, setShowCreateModal]);

    const renderData = useMemo(() => {
        if (editorData.length === 0 && selectedFile) {
            return <label className="h6 text-secondary">{translate("OTM_NO_LANGUAGES")}</label>
        }
        const filteredData = searchableData.filter((val) => matchesSearch(val));

        if (filteredData.length === 0) {
            return <label className="text-secondary h5 pt-2 ps-2">{translate("OTM_NO_KEY_VALUE")}</label>
        }

        return <>
            {renderDataHeader}
            <div id='scrollData' style={{ height: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                {editorData.map((val, key) => renderRow(val, key))}
            </div>
        </>;
    }, [translate, searchableData, editorData, selectedFile, renderDataHeader, matchesSearch, renderRow]);

    useEffect(() => {
        if (buildTimeConfig && buildTimeConfig[configKey] && editorData.length === 0) {
            setEditorData(() => {
                const data = [];
                const defaultTemplateData = templateConfig[configKey][defaultFile];
                const defaultOverridesData = buildTimeConfig[configKey][defaultFile];

                if (defaultTemplateData) {
                    Object.keys(defaultTemplateData).forEach(key => {
                        data.push({
                            key,
                            defaultParentValue: defaultTemplateData[key],
                            defaultValue: defaultOverridesData[key],
                            fixed: true
                        });
                    });
                }
                if (defaultOverridesData) {
                    Object.keys(defaultOverridesData).forEach(key => {
                        if (!data.find(obj => obj.key === key)) {
                            data.push({
                                key,
                                defaultValue: defaultOverridesData[key],
                                fixed: false
                            });
                        }
                    });
                }
                setSearchableData(data.map(entry => _.clone(entry)))
                return data;
            });
        }
    }, [buildTimeConfig, templateConfig, editorData]);

    useEffect(() => {
        if (keyAdded) {
            var elem = document.getElementById('scrollData');
            elem.scrollTop = elem.scrollHeight;
            setKeyAdded(false);
        }
    }, [keyAdded]);

    if (!buildTimeConfig || !templateConfig) {
        return <div className="col-2 pt-2 ps-2 h6">{translate("OTM_NO_CONFIG_DATA")}</div>
    }

    return <div className="row ps-3">
        <ConfirmationPrompt show={showRestoreModal} message={translate("OTM_REVERT_LOCAL_CHANGES")}
            onConfirm={handleRestoreConfirm}
            onCancel={() => setShowRestoreModal(false)} />
        <ConfirmationPrompt show={showDeleteModal} message={translate("OTM_DELETE_FILE?")}
            onConfirm={() => handleDeleteFile(selectedFile)}
            onCancel={() => setShowDeleteModal(false)} />
        <CreateModal
            show={showCreateModal}
            heading={"OTM_CREATE_FILE"}
            validationRegex={fileRegex}
            onCreate={handleAddFile}
            onCancel={() => setShowCreateModal(false)} />
        <Toolbar
            buttons={[
                {
                    name: "SettingsBackupRestore",
                    title: translate("OTM_RESTORE"),
                    onClick: handleRestore,
                },
                {
                    name: "Send",
                    title: translate("OTM_SEND_UPDATE"),
                    onClick: onLivePreviewUpdate,
                    disabled: !canSendUpdate
                }
            ]}
            heading={translate(heading)}
        />
        {searchBar}
        {renderData}
    </div>
}

export default LanguagesEditor;